import { DataFilterGroup, FieldFilter } from '@juvare/common-web-ui/DataFilter';
import { DataTable, DataTableColumn, DataTableService, ToolbarAction, ToolbarActions } from '@juvare/common-web-ui/DataTable';
import { showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { handleErrorSnackbar, showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import { parseISO } from 'date-fns';
import React from 'react';
import { useInboxClient, useInboxTranslation } from '../Hooks';
import { GetNotificationsRequest, NotificationListItem } from '../Models';
import { DetailsOptions, SlideoutInbox } from './Components/SlideoutInbox';

export type InboxType = 'received' | 'archived';

interface InboxOptions {
    details?: DetailsOptions;
}

export interface InboxInitialFilters {
    unread?: boolean;
    waitingForResponse?: boolean;
    searchText?: string;
    receivedFromTo?: {
        dateFrom?: Date;
        dateTo?: Date;
    };
}

export interface InboxProps {
    reloadFlag?: unknown;
    type?: InboxType;
    multiSelect?: boolean;
    options?: InboxOptions;
    initialFilters?: InboxInitialFilters;
}

export const Inbox: React.FC<InboxProps> = (props) => {
    const { t } = useInboxTranslation();
    const inboxClient = useInboxClient();
    const { type = 'received', multiSelect = false, initialFilters } = props;
    const [selectedNotification, setSelectedNotification] = React.useState<NotificationListItem>();
    const [selectedNotificationIds, setSelectedNotificationIds] = React.useState<string[]>([]);
    const [reloadFlag, setReloadFlag] = React.useState({});
    const service: DataTableService<NotificationListItem> = React.useMemo(
        () => ({
            id: (row) => row.id,
            list: (filter, sort, limit, offset) => {
                const request: GetNotificationsRequest = {
                    page: offset / limit + 1,
                    size: limit,
                };
                request.archived = type === 'archived';
                applyFilter(request, filter);
                if (sort[0].field === 'received') {
                    request.orderBy = 'received';
                    request.orderDirection = sort[0].desc ? 'desc' : 'asc';
                }
                return inboxClient.getList(request).then((response) => ({ total: response.totalCount, rows: response.items }));
            },
        }),
        [inboxClient, type],
    );
    const reloadList = React.useCallback(() => {
        setReloadFlag({});
    }, []);

    React.useEffect(() => {
        reloadList();
    }, [reloadList, props.reloadFlag]);

    React.useEffect(() => {
        setSelectedNotificationIds([]);
        setSelectedNotification(undefined);
    }, [reloadList, type]);

    React.useEffect(() => {
        if (selectedNotification?.id && !selectedNotification?.read) {
            inboxClient
                .markAsRead([selectedNotification.id])
                .then(() => reloadList())
                .catch(handleErrorSnackbar);
        }
    }, [selectedNotification?.id, selectedNotification?.read, reloadList, inboxClient]);

    const bulkAction = {
        label: t(`inboxTable.bulkActionFor.${type}` as const),
        'data-test': 'bulk-action-button',
        actionProps: {
            disabled: selectedNotificationIds.length === 0,
        },
        onClick: () => {
            if (type === 'received') {
                showConfirmDialog({
                    title: t('inboxTable.dialog.areYouSureYouWantToArchiveNotification', { count: selectedNotificationIds.length }),
                    onConfirm: () =>
                        inboxClient
                            .archive(selectedNotificationIds)
                            .then(() => {
                                reloadList();
                                setSelectedNotificationIds([]);
                                showAlertSnackbar({
                                    message: t('inboxTable.dialog.archivingCompletedSuccessfully'),
                                    autoHideDuration: 1000,
                                    type: 'success',
                                });
                            })
                            .catch(handleErrorSnackbar),
                });
            } else if (type === 'archived') {
                showConfirmDialog({
                    title: t('inboxTable.dialog.areYouSureYouWantToRestoreNotification', { count: selectedNotificationIds.length }),
                    onConfirm: () =>
                        inboxClient
                            .unArchive(selectedNotificationIds)
                            .then(() => {
                                reloadList();
                                setSelectedNotificationIds([]);
                                showAlertSnackbar({
                                    message: t('inboxTable.dialog.restoringCompletedSuccessfully'),
                                    autoHideDuration: 1000,
                                    type: 'success',
                                });
                            })
                            .catch(handleErrorSnackbar),
                });
            }
        },
    };

    const actions: ToolbarAction[] = [
        ...(multiSelect ? [bulkAction] : []),
        {
            label: t('inboxTable.markAllAsRead'),
            'data-test': 'mark-all-as-read-button',
            onClick: () =>
                showConfirmDialog({
                    title: t('inboxTable.dialog.areYouSureYouWantToMarkAllAsRead'),
                    onConfirm: () =>
                        inboxClient
                            .markAllAsRead(type === 'archived')
                            .then(() => {
                                reloadList();
                                showAlertSnackbar({
                                    message: t('inboxTable.dialog.markAllAsReadCompletedSuccessfully'),
                                    autoHideDuration: 1000,
                                    type: 'success',
                                });
                            })
                            .catch(handleErrorSnackbar),
                }),
        },
    ];

    const columns: DataTableColumn<NotificationListItem>[] = React.useMemo(
        () => [
            {
                name: 'notification',
                label: t('inboxTable.columns.notification'),
                render: (row) => {
                    let type = row.labels?.join(', ');
                    if (type) {
                        type += ': ';
                    }
                    return (
                        <>
                            <Typography
                                variant='body2'
                                sx={{
                                    color: (theme) => theme.palette.text.secondary,
                                    display: 'inline',
                                }}
                            >
                                {wrapUnread(type, !!row.read)}
                            </Typography>
                            <Typography variant='body2' sx={{ display: 'inline' }}>
                                {wrapUnread(row.subject, !!row.read)}
                            </Typography>
                        </>
                    );
                },
            },
            {
                name: 'sender',
                label: t('inboxTable.columns.sender'),
                render: (row) => wrapUnread(row.sender?.displayName, !!row.read),
            },
            {
                name: 'received',
                label: t('inboxTable.columns.received'),
                sortable: true,
                render: (row) => wrapUnread(row.received && t('dateTime', { value: parseISO(row.received) }), !!row.read),
            },
        ],
        [t],
    );
    const mobileColumns: DataTableColumn<NotificationListItem>[] = React.useMemo(
        () => [
            {
                name: 'notification',
                label: t('inboxTable.columns.notification'),
                render: (row) => {
                    let type = row.labels?.join(', ');
                    if (type) {
                        type += ': ';
                    }
                    return (
                        <>
                            <Typography
                                variant='body2'
                                sx={{
                                    color: (theme) => theme.palette.text.secondary,
                                    display: 'inline',
                                }}
                            >
                                {wrapUnread(type, !!row.read)}
                            </Typography>
                            <Typography variant='body2' sx={{ display: 'inline' }}>
                                {wrapUnread(row.subject, !!row.read)}
                            </Typography>
                        </>
                    );
                },
            },
            {
                name: 'received',
                label: t('inboxTable.columns.received'),
                sortable: true,
                render: (row) => (
                    <>
                        {row.sender?.displayName && (
                            <Typography
                                variant='body2'
                                sx={{
                                    color: (theme) => theme.palette.text.secondary,
                                }}
                            >
                                {wrapUnread(t('inboxTable.columns.senderRecord', { name: row.sender.displayName }), !!row.read)}
                            </Typography>
                        )}
                        <Typography variant='body2'>
                            {wrapUnread(row.received && t('dateTime', { value: parseISO(row.received) }), !!row.read)}
                        </Typography>
                    </>
                ),
            },
        ],
        [t],
    );
    const filterGroups: DataFilterGroup[] = React.useMemo(
        () => [
            {
                label: t('inboxTable.filter.received'),
                configs: [
                    {
                        field: 'receivedFromTo',
                        type: 'date-from-to',
                        fromLabel: t('inboxTable.filter.from'),
                        toLabel: t('inboxTable.filter.to'),
                        clearable: true,
                        initialValue: initialFilters?.receivedFromTo,
                    },
                ],
            },
            {
                label: t('inboxTable.filter.show'),
                configs: [
                    {
                        field: 'unread',
                        type: 'boolean',
                        label: t('inboxTable.filter.unread'),
                        initialValue: initialFilters?.unread,
                    },
                    {
                        field: 'waitingForResponse',
                        type: 'boolean',
                        label: t('inboxTable.filter.waitingForResponse'),
                        initialValue: initialFilters?.waitingForResponse,
                    },
                ],
            },
        ],
        [t, initialFilters],
    );

    return (
        <>
            <DataTable
                service={service}
                columns={columns}
                mobileColumns={mobileColumns}
                initialSortColumnName='received'
                initialSortColumnDesc={true}
                filterGroups={filterGroups}
                onSelectRow={setSelectedNotification}
                fullTextSearchField='searchText'
                fullTextSearchInitialValue={initialFilters?.searchText}
                reloadFlag={reloadFlag}
                data-test='inbox-ui-table'
                multiSelect={multiSelect}
                multiSelectedRowIds={selectedNotificationIds}
                onMultiSelectChange={(rowIds: string[]) => setSelectedNotificationIds(rowIds)}
                toolbarComponent={(props) => {
                    return (
                        <>
                            <ToolbarActions {...props} actions={actions} />
                            <IconButton color='primary' onClick={reloadList} data-test='reload-button'>
                                <CachedOutlinedIcon />
                            </IconButton>
                        </>
                    );
                }}
            />
            <SlideoutInbox
                notificationId={selectedNotification?.id}
                setSelectedNotification={setSelectedNotification}
                reloadList={reloadList}
                options={props.options?.details}
            />
        </>
    );
};

function applyFilter(request: GetNotificationsRequest, filter: FieldFilter[]) {
    const filterValueMap = filter.reduce((acc, value) => acc.set(value.field, value.value), new Map());
    if (filterValueMap.has('searchText')) {
        request.searchText = filterValueMap.get('searchText');
    }
    if (filterValueMap.has('receivedFromTo')) {
        const range = filterValueMap.get('receivedFromTo');
        if (!isNaN(range.dateFrom)) {
            request.receivedFrom = range.dateFrom.toISOString();
        }
        if (!isNaN(range.dateTo)) {
            request.receivedUntil = range.dateTo.toISOString();
        }
    }

    if (filterValueMap.has('unread') && !!filterValueMap.get('unread')) {
        request.read = false;
    }
    if (filterValueMap.has('waitingForResponse')) {
        request.waitingForResponse = filterValueMap.get('waitingForResponse');
    }
}

function wrapUnread(text: string | undefined, read: boolean): React.ReactElement | null {
    if (text) {
        return (
            <Box
                component='span'
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    WebkitLineClamp: 4,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    fontWeight: read ? undefined : 'bold',
                }}
            >
                {text}
            </Box>
        );
    }
    return null;
}
